import { paymentMethods } from "constants/payments";

import {
  DEVICE_TAG_VALUES,
  type EarSlot,
  type Field,
  paEarTag,
  paKindTotalTag,
  withDeviceGroups
} from "./purchase-agreement-device-tags.utils";
import { type MergeTagMenuEntry } from "./signature-merge-tags.utils";

/*
 * ENG-2492: every value a purchase agreement prints, as its own merge tag, so a converted paper agreement
 * can fill one blank per value. Gated on `digital-form-tags-purchase-agreement`: only
 * `withPurchaseAgreementTags` adds these entries, so with the flag off the picker and the converter
 * catalog are master's exactly.
 * Value handlers are always registered (merge-tags-values/purchase-agreement-*-values.utils.ts).
 *
 * Device tags (per kind and ear) are specified in purchase-agreement-device-tags.utils.ts; this file adds
 * the services, totals, payments and sale details, and assembles the menu.
 */

/** ENG-2501 names, kept for the hearing-aid tags' callers. */
export const paHearingAidTag = (slot: EarSlot, fieldTag: string) =>
  paEarTag("HEARING_AID", slot, fieldTag);
export const paBothHearingAidsTag = (fieldTag: string) =>
  paKindTotalTag("HEARING_AID", fieldTag);

// ENG-2502: the money section. Each is `Patient.SelectedInvoice.<tag>`.
const TOTALS_FIELDS: Field[] = [
  // Totals rows of the invoice table that had no tag of their own.
  { tag: "TotalDiscounts", label: "Total Discounts" },
  { tag: "TotalTax", label: "Total Tax" },
  { tag: "AmountDueFromInsurance", label: "Amount Due From Insurance" },
  { tag: "AmountDueFromPatient", label: "Amount Due From Patient" },
  // Values Anvil's purchase agreement prints.
  { tag: "Subtotal", label: "Subtotal Before Discounts And Tax" },
  { tag: "PatientResponsibilityTotal", label: "Patient Responsibility Total" },
  {
    tag: "InsuranceResponsibilityTotal",
    label: "Insurance Responsibility Total"
  },
  { tag: "AgreementDevices.Total", label: "Agreement Devices - Total" },
  {
    tag: "AgreementDevices.Subtotal",
    label: "Agreement Devices - Subtotal Before Discounts And Tax"
  },
  { tag: "AgreementDevices.Discount", label: "Agreement Devices - Discount" },
  { tag: "AgreementDevices.Tax", label: "Agreement Devices - Tax" },
  {
    tag: "AgreementDevices.TotalWithoutTax",
    label: "Agreement Devices - Total Without Tax"
  },
  {
    tag: "AgreementDevices.PatientResponsibility",
    label: "Agreement Devices - Patient Responsibility"
  },
  {
    tag: "AgreementDevices.InsuranceResponsibility",
    label: "Agreement Devices - Insurance Responsibility"
  },
  {
    tag: "AgreementDevices.TotalMinusInsuranceBenefit",
    label: "Agreement Devices - Total Minus Insurance Benefit"
  },
  { tag: "DevicesAndServices.Total", label: "Devices And Services - Total" },
  {
    tag: "DevicesAndServices.TotalWithoutTax",
    label: "Devices And Services - Total Without Tax"
  },
  { tag: "ServicesTotalWithoutTax", label: "Services Total Without Tax" },
  { tag: "BalanceDueOnAgreement", label: "Patient Balance Due On Agreement" },
  {
    tag: "PatientBalanceDueOnInvoice",
    label: "Patient Balance Due On Invoice"
  }
];

// A tag name is stored in saved forms, so each method's name is fixed here rather than derived from its
// label: rewording a label must not rename a tag.
const PAID_BY_TAG_NAMES: Record<number, string> = {
  1: "Cash",
  2: "CreditDebitCard",
  3: "DirectDeposit",
  4: "Check",
  5: "FinancingCareCredit",
  6: "FinancingHealthiPlan",
  7: "FinancingWellsFargo",
  8: "Online",
  9: "EFT",
  10: "PatientCredit",
  11: "Allegro",
  12: "Visa",
  13: "Mastercard",
  14: "AmericanExpress",
  15: "Discover",
  16: "ACH",
  17: "Zelle",
  18: "Venmo",
  19: "FinancingCherry",
  20: "Square",
  21: "InsuranceCredit",
  99: "Other"
};

// One tick per payment method Suno records: `PaidBy.<method>` prints "X" when a patient payment used it.
export const PAID_BY_METHODS = Object.entries(PAID_BY_TAG_NAMES).map(
  ([code, name]) => ({
    code: Number(code),
    tag: `PaidBy.${name}`,
    label: `Paid By ${paymentMethods[Number(code) as keyof typeof paymentMethods]}`
  })
);

const PAYMENTS_FIELDS: Field[] = [
  { tag: "TotalPatientPayments", label: "Total Patient Payments" },
  { tag: "TotalSurcharge", label: "Total Surcharge" },
  { tag: "TotalCharged", label: "Total Charged" },
  { tag: "TotalInsurancePayments", label: "Total Insurance Payments" },
  { tag: "TotalPayments", label: "Total Payments" },
  { tag: "PaymentMethods", label: "Payment Methods" },
  { tag: "LastPatientPayment.Method", label: "Last Patient Payment - Method" },
  {
    tag: "LastPatientPayment.CheckNumber",
    label: "Last Patient Payment - Check Number"
  },
  {
    tag: "LastPatientPayment.BankName",
    label: "Last Patient Payment - Bank Name"
  },
  {
    tag: "LastPatientPayment.Description",
    label: "Last Patient Payment - Description"
  },
  ...PAID_BY_METHODS.map(({ tag, label }) => ({ tag, label }))
];

const SALE_DETAILS_FIELDS: Field[] = [
  { tag: "Insurer", label: "Insurer" },
  { tag: "ManagedCarePlan", label: "Managed Care Plan" },
  { tag: "InsurancePolicy.Copay", label: "Insurance Policy - Copay" },
  {
    tag: "InsurancePolicy.HearingAidBenefit",
    label: "Insurance Policy - Hearing Aid Benefit"
  },
  { tag: "InsurancePolicy.Deductible", label: "Insurance Policy - Deductible" },
  { tag: "ProviderNPI", label: "Provider - NPI" },
  {
    tag: "ProviderFullNameWithTitleAndSuffix",
    label: "Provider - Full Name With Title And Suffix"
  }
];

const invoiceEntry = (field: Field) => ({
  title: `Patient - Invoice - ${field.label}`,
  value: `Patient.SelectedInvoice.${field.tag}`
});
export const paInvoiceTag = (field: string) =>
  `Patient.SelectedInvoice.${field}`;

export const PA_TOTALS_FIELD_TAGS = TOTALS_FIELDS.map(({ tag }) => tag);
export const PA_PAYMENTS_FIELD_TAGS = PAYMENTS_FIELDS.map(({ tag }) => tag);
export const PA_SALE_DETAILS_FIELD_TAGS = SALE_DETAILS_FIELDS.map(
  ({ tag }) => tag
);

// ENG-2503: services and dispensing fees by their place in the invoice table, and Anvil's V-code service.
export const PA_SERVICE_SLOT_COUNT = 5;

const SERVICE_FIELDS: Field[] = [
  { tag: "Product", label: "Product" },
  { tag: "Name", label: "Name (as in your Catalog)" },
  { tag: "Description", label: "Description" },
  { tag: "CPTCode", label: "CPT Code" },
  { tag: "Quantity", label: "Quantity" },
  { tag: "UnitPrice", label: "Unit Price" },
  { tag: "ListPrice", label: "List Price" },
  { tag: "Discount", label: "Discount" },
  { tag: "Price", label: "Price After Discount" },
  { tag: "TotalWithTax", label: "Total With Tax" },
  { tag: "PatientResponsibility", label: "Patient Responsibility" },
  { tag: "InsuranceResponsibility", label: "Insurance Responsibility" }
];

const V_CODE_SERVICE_FIELDS: Field[] = [
  { tag: "Name", label: "Name (as in your Catalog)" },
  { tag: "Description", label: "Description" },
  { tag: "CPTCode", label: "CPT Code" },
  { tag: "UnitPrice", label: "Unit Price" },
  { tag: "Discount", label: "Discount" },
  { tag: "TotalWithTax", label: "Total With Tax" }
];

export const paServiceTag = (position: number, field: string) =>
  `Patient.SelectedInvoice.Service${position}.${field}`;
export const paVCodeServiceTag = (field: string) =>
  `Patient.SelectedInvoice.VCodeService.${field}`;
export const PA_SERVICE_FIELD_TAGS = SERVICE_FIELDS.map(({ tag }) => tag);
export const PA_V_CODE_SERVICE_FIELD_TAGS = V_CODE_SERVICE_FIELDS.map(
  ({ tag }) => tag
);

const SERVICE_GROUPS: MergeTagMenuEntry[] = Array.from(
  { length: PA_SERVICE_SLOT_COUNT },
  (_, index) => {
    const position = index + 1;
    return {
      title: `Service ${position}`,
      menu: SERVICE_FIELDS.map((field) => ({
        title: `Patient - Invoice - Service ${position} - ${field.label}`,
        value: paServiceTag(position, field.tag)
      }))
    };
  }
);
const V_CODE_SERVICE: MergeTagMenuEntry = {
  title: "First V-Code Service",
  menu: V_CODE_SERVICE_FIELDS.map((field) => ({
    title: `Patient - Invoice - First V-Code Service - ${field.label}`,
    value: paVCodeServiceTag(field.tag)
  }))
};
const SERVICE_VALUES = [...SERVICE_GROUPS, V_CODE_SERVICE].flatMap(
  (group) => group.menu ?? []
);

const TOTALS = TOTALS_FIELDS.map(invoiceEntry);
const PAYMENTS = PAYMENTS_FIELDS.map(invoiceEntry);
const SALE_DETAILS = SALE_DETAILS_FIELDS.map(invoiceEntry);

export const PA_TAG_VALUES = new Set<string>([
  ...DEVICE_TAG_VALUES,
  ...[...TOTALS, ...PAYMENTS, ...SALE_DETAILS, ...SERVICE_VALUES].map(
    ({ value }) => value as string
  )
]);

export const withPurchaseAgreementTags = (
  menu: MergeTagMenuEntry[]
): MergeTagMenuEntry[] => [
  ...withDeviceGroups(menu),
  { title: "Services", menu: [...SERVICE_GROUPS, V_CODE_SERVICE] },
  { title: "Totals", menu: TOTALS },
  { title: "Payments", menu: PAYMENTS },
  { title: "Sale Details", menu: SALE_DETAILS }
];
